import _objectSpread from "/builds/huggg/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import { translateObject, translateObjectInverse } from '@huggg/bar-staff';
import { prop } from 'ramda';
import { urls } from '../config';
import getCursorPaginationParams from '../utils/getCursorPaginationParams';
import withAxios from '../utils/with-axios';
import { brandMap, cursorPaginatedResponseMap, productMap, superhugggMap, transactionMap } from '../translationMaps';
var DEFAULT_PER_PAGE = 20;
function formatTransaction(data) {
  var transaction = data.data,
    embedded = data.embedded;
  var translated = translateObject(transactionMap, transaction);
  if (translated.superhugggId) {
    translated.superhuggg = embedded.super_hugggs.find(function (item) {
      return item.id = translated.superhugggId;
    });
  }
  return translated;
}
function formatTopupsResponse(data) {
  var translatedPagination = translateObject(cursorPaginatedResponseMap, data);
  if (translatedPagination.data.length) {
    translatedPagination.data = translatedPagination.data.map(function (item) {
      return formatTransaction({
        data: item
      });
    });
  }
  return translatedPagination;
}

/**
 * Get all available transactions for the current user, with potential filters.
 *
 * NOTE: Does not handle any errors, left up to the calling method.
 *
 * transactions.all().then(data => { ... })
 * transactions.all(axiosInstance).then(data => { ... })
 * transactions.all({page: 2}).then(data => { ... })
 * transactions.all(null, ['huggg']).then(data => { ... })
 *
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {Object[]} [options={}] Options to supply the endpoint
 * @param {Object|null} [options[].filter=null] Filter the data before returning.
 * @param {Number} [options[].page=1] Which page of data to fetch.
 * @param {Number} [options[].per_page=DEFAULT_PER_PAGE]
 *     How many items to  display per page
 * @returns {Promise} Unresolved Promise from the API.
 */
function all($axios) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var defaultOptions = {
    filter: null,
    page: 1,
    per_page: DEFAULT_PER_PAGE
  };
  var params = _objectSpread(_objectSpread({}, defaultOptions), options);
  return $axios.get(urls.apiV2('/transactions'), {
    params: params
  }).then(prop('data')).then(function (response) {
    return _objectSpread(_objectSpread({}, response), {}, {
      data: response.data.map(function (transaction) {
        return _objectSpread(_objectSpread({}, translateObject(transactionMap, transaction)), {}, {
          brand: translateObject(brandMap, transaction.brand || {}),
          product: translateObject(productMap, transaction.product || {})
        });
      })
    });
  });
}

/**
 * Fetch data for a single transaction at the supplied ID.
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to fetch data
 * @returns {Promise} Unresolved Promise from the API.
 */
function single($axios, id) {
  return $axios.get(urls.apiV2("/transactions/".concat(id)), {
    params: {
      embed: ['super_huggg.purchase']
    }
  }).then(prop('data')).then(formatTransaction);
}

/**
 * Updates notes, hugggExpiry or superhugggExpiry for a transaction
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to fetch data
 * @param {Object} data Data object to update the transaction
 * @returns {Promise} Unresolved Promise from the API.
 */
function update($axios, id, data) {
  var formatted = translateObjectInverse(transactionMap, data);
  var superhuggg = data.superhuggg ? {
    superhuggg: translateObjectInverse(superhugggMap, data.superhuggg)
  } : {};
  return $axios.patch(urls.apiV2("/transactions/".concat(id)), _objectSpread(_objectSpread({}, formatted), superhuggg)).then(formatTransaction);
}

/**
 * Asynchronously updates hugggExpiry for all hugggs in a transaction
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to fetch data
 * @param {Object} data Data object to update the transaction
 * @returns {Promise} Unresolved Promise from the API.
 */
function updateHugggs($axios, id, data) {
  return $axios.patch(urls.apiV2("/transactions/".concat(id, "/hugggs")), data);
}

/**
 * Cancels a transaction
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to cancel
 * @returns {Promise} Unresolved Promise from the API.
 */
function cancel($axios, id) {
  return $axios.post(urls.apiV2("/transactions/".concat(id, "/cancel")), {}).then(formatTransaction);
}

/**
 * Cancels a transaction hugggs asynchronously
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID of the hugggs to cancel
 * @returns {Promise} Unresolved Promise from the API.
 */
function cancelHugggs($axios, id) {
  return $axios.post(urls.apiV2("/transactions/".concat(id, "/hugggs/cancel")), {});
}

/**
 * Sets an uncompleted transaction to notify on completion
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to fetch data
 * @returns {Promise} Unresolved Promise from the API.
 */
function notifyOnComplete($axios, id) {
  return $axios.post(urls.apiV2("/transactions/".concat(id, "/notify")), {}).then(formatTransaction);
}

/**
 * Toggles the sent date for all hugggs in a transaction
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to fetch data
 * @param {Object} data Data object to update the transaction
 * @returns {Promise} Unresolved Promise from the API.
 */
function toggleHugggsSentDate($axios, id, data) {
  var sent = data.sent;
  return $axios.post(urls.apiV2("/transactions/".concat(id, "/hugggs/toggleSentDate")), {
    sent: sent
  }).then(prop('data'));
}

/**
 * Sends hugggs in a transaction to a specified set of receivers
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} id Transaction ID to fetch data
 * @param {Object} data Data object containing an array of receivers
 * @returns {Promise} Unresolved Promise from the API.
 */
function sendHugggs($axios, id, data) {
  var receivers = data.receivers;
  return $axios.post(urls.apiV2("/transactions/".concat(id, "/hugggs/send")), {
    receivers: receivers
  }).then(prop('data'));
}

/**
 * Fetch all transactions for a given user ID.
 *
 * @param {Object} $axios [$axios=axios] Axios instance.
 * @param {String} userId
 * @param {Object[]} options
 * @param {Number} [options[].usePagination=true] Whether to use pagination
 * @param {Number} [options[].limit=DEFAULT_PER_PAGE]
 *     How many items to  display per page
 * @param {String} [options[].next] Cursor to paginate forwards from
 * @param {String} [options[].previous] Cursor to paginate backwards from
 * @returns {Promise} Unresolved Promise from the API.
 */
function allTopUpsByUserId($axios, userId) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (!userId) {
    throw new Error('Prop `userId` is required in `transactions.allTopUpsByUserId`');
  }
  var defaultParams = {
    use_pagination: true,
    limit: DEFAULT_PER_PAGE
  };
  var params = getCursorPaginationParams(options, defaultParams);
  return $axios.get(urls.apiV2("/transaction/top-ups/".concat(userId)), {
    params: params
  }).then(prop('data')).then(formatTopupsResponse);
}
export default {
  all: withAxios(all),
  allTopUpsByUserId: withAxios(allTopUpsByUserId),
  single: withAxios(single),
  update: withAxios(update),
  updateHugggs: withAxios(updateHugggs),
  cancel: withAxios(cancel),
  notifyOnComplete: withAxios(notifyOnComplete),
  toggleHugggsSentDate: withAxios(toggleHugggsSentDate),
  sendHugggs: withAxios(sendHugggs),
  cancelHugggs: withAxios(cancelHugggs)
};